"use client";

import type { ScalePlacement, ScaleQuality } from "@/lib/virtuosa-layout/place-scale";

const ROOT_NAMES = [
  "C",
  "C♯",
  "D",
  "D♯",
  "E",
  "F",
  "F♯",
  "G",
  "G♯",
  "A",
  "A♯",
  "B",
] as const;

const QUALITIES: { id: ScaleQuality; label: string }[] = [
  { id: "major", label: "majeure" },
  { id: "minor", label: "mineure" },
];

interface ScalePickerProps {
  rootSemi: number;
  quality: ScaleQuality;
  octave: number;
  validOctaves: readonly number[];
  placement: ScalePlacement | null;
  onRootChange: (semi: number) => void;
  onQualityChange: (quality: ScaleQuality) => void;
  onOctaveChange: (octave: number) => void;
}

export function ScalePicker({
  rootSemi,
  quality,
  octave,
  validOctaves,
  placement,
  onRootChange,
  onQualityChange,
  onOctaveChange,
}: ScalePickerProps) {
  return (
    <div className="space-y-3">
      <div>
        <div className="mb-1.5 text-[10px] uppercase tracking-wider text-text-muted">
          Tonique
        </div>
        <div className="flex flex-wrap gap-1">
          {ROOT_NAMES.map((name, i) => (
            <button
              key={name}
              type="button"
              onClick={() => onRootChange(i)}
              className={`min-w-[34px] rounded-md border px-2 py-1 font-mono text-xs transition-all duration-100 cursor-pointer ${
                rootSemi === i
                  ? "border-accent bg-accent/20 text-accent-bright"
                  : "border-border-subtle bg-bg-elevated/60 text-text-secondary hover:bg-bg-hover hover:text-text-primary"
              }`}
            >
              {name}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-1">
            {QUALITIES.map((q) => (
              <button
                key={q.id}
                type="button"
                onClick={() => onQualityChange(q.id)}
                className={`rounded-md border px-2.5 py-1 font-mono text-xs transition-all duration-100 cursor-pointer ${
                  quality === q.id
                    ? "border-accent bg-accent/20 text-accent-bright"
                    : "border-border-subtle bg-bg-elevated/60 text-text-secondary hover:bg-bg-hover hover:text-text-primary"
                }`}
              >
                {q.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1">
            <span className="text-[10px] uppercase tracking-wider text-text-muted">
              Octave
            </span>
            <div className="flex gap-1">
              {validOctaves.map((oct) => (
                <button
                  key={oct}
                  type="button"
                  onClick={() => onOctaveChange(oct)}
                  className={`min-w-[28px] rounded-md border px-2 py-1 font-mono text-xs transition-all duration-100 cursor-pointer ${
                    octave === oct
                      ? "border-accent bg-accent/20 text-accent-bright"
                      : "border-border-subtle bg-bg-elevated/60 text-text-secondary hover:bg-bg-hover hover:text-text-primary"
                  }`}
                >
                  {oct}
                </button>
              ))}
            </div>
          </div>
        </div>

        <ScaleFamily rootSemi={rootSemi} placement={placement} />
      </div>
    </div>
  );
}

// The old figures here — Notes, Rangées, Distance — were lattice metrics, and
// the interesting one was missing. A scale's shape does not depend on its key,
// it depends on which of the three row families the tonic belongs to: the four
// tonics of a family trace exactly the same path. Saying so turns "why does
// this one look different" into the rule that answers it.
function ScaleFamily({
  rootSemi,
  placement,
}: {
  rootSemi: number;
  placement: ScalePlacement | null;
}) {
  if (!placement) {
    return (
      <span className="text-[11px] italic text-err">gamme injouable</span>
    );
  }

  const family = ((rootSemi % 3) + 3) % 3;
  const siblings = [0, 1, 2, 3]
    .map((i) => ROOT_NAMES[(family + i * 3) % 12])
    .join(" · ");

  return (
    <span className="text-[11px] text-text-muted">
      Même forme pour{" "}
      <span className="font-mono text-text-secondary">{siblings}</span>
    </span>
  );
}
